import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Fact rendering callback type
type RenderFactBadge = (factId: string, key: string) => React.ReactNode;

interface ChatMarkdownRendererProps {
  content: string;
  /** Optional callback to render fact badges. If provided, [[FACT:id]] placeholders will be replaced with badges. */
  renderFactBadge?: RenderFactBadge;
}

/**
 * Process React children to replace [[FACT:id]] placeholders with fact badge components.
 * This preserves the original markdown structure while injecting inline fact badges.
 */
function processChildrenForFacts(
  children: React.ReactNode,
  renderFactBadge: RenderFactBadge,
  keyPrefix: string = ''
): React.ReactNode {
  return React.Children.map(children, (child, index) => {
    // If it's a string, check for fact placeholders
    if (typeof child === 'string') {
      const factPattern = /\[\[FACT:([^\]]+)\]\]|\[\[FACT_LOADING(?::\d+)?\]\]/g;

      if (!factPattern.test(child)) {
        return child;
      }

      // Reset regex state
      factPattern.lastIndex = 0;

      // Split the string and replace fact placeholders
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      let partKey = 0;

      while ((match = factPattern.exec(child)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          parts.push(child.substring(lastIndex, match.index));
        }

        // Add the fact badge
        if (match[0].startsWith('[[FACT_LOADING')) {
          // Loading state - render a simple placeholder
          parts.push(
            <span key={`${keyPrefix}-${index}-loading-${partKey++}`} className="inline-flex items-center justify-center w-5 h-5 mx-0.5 text-xs bg-gray-200 text-gray-400 rounded-full animate-pulse">
              …
            </span>
          );
        } else {
          // Actual fact - render via callback
          const factId = match[1];
          parts.push(renderFactBadge(factId, `${keyPrefix}-${index}-fact-${partKey++}`));
        }

        lastIndex = factPattern.lastIndex;
      }

      // Add remaining text
      if (lastIndex < child.length) {
        parts.push(child.substring(lastIndex));
      }

      return <>{parts}</>;
    }

    // If it's a React element, recursively process its children
    if (React.isValidElement(child)) {
      const childElement = child as React.ReactElement<{ children?: React.ReactNode }>;
      if (childElement.props.children) {
        return React.cloneElement(childElement, {
          ...childElement.props,
          children: processChildrenForFacts(childElement.props.children, renderFactBadge, `${keyPrefix}-${index}`),
        });
      }
    }

    return child;
  });
}

/**
 * ChatMarkdownRenderer - A ReactMarkdown wrapper optimized for chat UI
 * Renders GitHub-Flavored Markdown with styling appropriate for inline chat messages.
 * Uses React.memo to prevent unnecessary re-renders during streaming.
 *
 * When renderFactBadge is provided, [[FACT:id]] placeholders in the text are automatically
 * replaced with inline fact badge components, preserving the markdown structure.
 */
function ChatMarkdownRendererComponent({ content, renderFactBadge }: ChatMarkdownRendererProps): JSX.Element {
  if (!content || !content.trim()) {
    return <>{content}</>;
  }

  // Helper to wrap children with fact processing if renderFactBadge is provided
  const wrapChildren = (children: React.ReactNode, key: string = 'root') => {
    if (renderFactBadge) {
      return processChildrenForFacts(children, renderFactBadge, key);
    }
    return children;
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Headers - render as bold text with size scaling, using div with ARIA heading roles for accessibility
        h1: ({ children }) => (
          <div
            role="heading"
            aria-level={1}
            className="block text-lg font-bold text-cvc-text mt-3 mb-2 first:mt-0"
          >
            {wrapChildren(children, 'h1')}
          </div>
        ),
        h2: ({ children }) => (
          <div
            role="heading"
            aria-level={2}
            className="block text-base font-bold text-cvc-text mt-2 mb-1.5"
          >
            {wrapChildren(children, 'h2')}
          </div>
        ),
        h3: ({ children }) => (
          <div
            role="heading"
            aria-level={3}
            className="block text-sm font-bold text-cvc-text mt-2 mb-1"
          >
            {wrapChildren(children, 'h3')}
          </div>
        ),
        // Paragraphs - standard text with proper spacing
        p: ({ children }) => (
          <p className="block mb-2 last:mb-0">{wrapChildren(children, 'p')}</p>
        ),
        // Lists - proper bullet/number styling
        ul: ({ children }) => (
          <ul className="block list-disc list-outside ml-5 mb-2 space-y-1">
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="block list-decimal list-outside ml-5 mb-2 space-y-1">
            {children}
          </ol>
        ),
        li: ({ children }) => (
          <li className="leading-relaxed">{wrapChildren(children, 'li')}</li>
        ),
        // Text formatting
        strong: ({ children }) => (
          <strong className="font-semibold">{wrapChildren(children, 'strong')}</strong>
        ),
        em: ({ children }) => (
          <em className="italic">{wrapChildren(children, 'em')}</em>
        ),
        // Code - inline and block
        code: ({ children, className }) => {
          // Check if this is a code block (has language class) vs inline code
          const isCodeBlock = className && className.startsWith('language-');
          if (isCodeBlock) {
            return (
              <code className="block bg-gray-100 text-gray-800 px-3 py-2 rounded text-xs font-mono overflow-x-auto my-2">
                {children}
              </code>
            );
          }
          // Inline code
          return (
            <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-xs font-mono">
              {children}
            </code>
          );
        },
        // Code blocks (pre wrapper)
        pre: ({ children }) => (
          <div className="block my-2">
            {children}
          </div>
        ),
        // Blockquotes
        blockquote: ({ children }) => (
          <blockquote className="block border-l-4 border-gray-300 pl-3 my-2 italic text-gray-600">
            {children}
          </blockquote>
        ),
        // Links - open in new tab
        a: ({ href, children }) => {
          const safeHref =
            typeof href === 'string' && /^https?:\/\//i.test(href) ? href : undefined;

          if (!safeHref) {
            // Render non-clickable styled text if URL is not a safe http/https link
            return <span className="text-cvc-blue">{children}</span>;
          }

          return (
            <a
              href={safeHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cvc-blue hover:underline"
            >
              {children}
            </a>
          );
        },
        // Horizontal rule
        hr: () => (
          <hr className="block border-t border-gray-200 my-3" />
        ),
        // Tables (fallback - main tables handled by MarkdownTable component)
        table: ({ children }) => (
          <div className="block overflow-x-auto my-2">
            <table className="min-w-full border-collapse text-sm">
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-gray-50">{children}</thead>
        ),
        tbody: ({ children }) => (
          <tbody className="divide-y divide-gray-200">{children}</tbody>
        ),
        tr: ({ children }) => (
          <tr>{children}</tr>
        ),
        th: ({ children }) => (
          <th className="px-2 py-1 text-left font-semibold text-gray-700 border-b border-gray-300">
            {wrapChildren(children, 'th')}
          </th>
        ),
        td: ({ children }) => (
          <td className="px-2 py-1 text-gray-600 border-b border-gray-100">
            {wrapChildren(children, 'td')}
          </td>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

// Memoize to prevent re-renders during streaming when content hasn't changed
const ChatMarkdownRenderer = React.memo(ChatMarkdownRendererComponent);

export default ChatMarkdownRenderer;
